import { Device, UpdateDeviceRequest } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllDevicesCache } from '../queries/devices-cache';

type UseUpdateDeviceActionProps = {
    deviceId: string;
};

type UseUpdateDeviceActionData = {
    updateDevice: (data: UpdateDeviceRequest) => Promise<Device>;
    loading: boolean;
    error: string;
};

/**
 * Renaming a device and moving it to another team or member.
 *
 * No `isAllowed`/`reason`: whether a viewer may edit a device at all, and which
 * teams they may move it to, is decided a screen up by `useDevicePermissions`
 * for the *device's* company — the standalone device page sits outside the
 * selected one — and reaches the form as `allowedTeamIds`.
 */
export function useUpdateDeviceAction(
    props: UseUpdateDeviceActionProps
): UseUpdateDeviceActionData {
    const { deviceId } = props;

    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `PATCH /v3/devices/{deviceId}` declares gets a branch, bar
    // `COMPANY_NOT_FOUND`, which this namespace has nothing of its own to say
    // about and so falls through to the generic message.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'INVALID_TEAM')) {
                return t('shared.errors.team_not_found');
            }
            // The member picker is scoped to the selected team by the server,
            // so this only surfaces if the team changed underneath the form.
            if (isApiError(error, 'INVALID_USER')) {
                return t('shared.errors.member_not_in_team');
            }
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: UpdateDeviceRequest) =>
            openApiRef.current.devices
                .updateDevice(deviceId, data)
                .then((response) => response.data.data.device),
        onSuccess: (device) => {
            addOrUpdateAllDevicesCache(queryClient, device);

            UIToast.success(t('actions.update_device.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        updateDevice: (data) => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
